import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, H3, InputLabel, Select, TextArea, TextInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';

// TanStack Query hooks
import { useCustomersQuery, Customer } from '@api/queries/customers';
import {
  useCreateDeliveryDestinationMutation,
  useUpdateDeliveryDestinationMutation,
  useDeleteDeliveryDestinationMutation,
  DeliveryDestination
} from '@api/queries/deliveryDestinations';

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

const colourStyles = {
  multiValueLabel: (styles: any, obj: { data: { isActive: boolean } }) => ({
    backgroundColor: obj.data.isActive ? 'inherit' : '#feefb3',
    color: obj.data.isActive ? 'inherit' : '#9f6000'
  })
};

interface Props {
  deliveryDestinationToEdit?: DeliveryDestination | null;
  onClose: () => void;
}

const defaultDeliveryDestination: DeliveryDestination = {
  id: null,
  name: '',
  name2: '',
  notes: '',
  customerId: null,
  customerName: null,
  code: '',
  reference: '',
  streetAddress: '',
  postalCode: '',
  city: '',
  country: '',
  created: null,
  modified: null
};

const mapCustomerToOptions = (c: Customer) => ({ value: c.id, label: c.name });

const DeliveryDestinationEditor: React.FC<Props> = ({ deliveryDestinationToEdit, onClose }) => {
  const { t } = useTranslation();

  // Local form state
  const [deliveryDestination, setDeliveryDestination] = useState<DeliveryDestination>(
    deliveryDestinationToEdit || { ...defaultDeliveryDestination }
  );

  // TanStack Query for reference data
  const { data: customers = [], isLoading: isLoadingCustomers } = useCustomersQuery();

  // TanStack Query mutations
  const createMutation = useCreateDeliveryDestinationMutation();
  const updateMutation = useUpdateDeliveryDestinationMutation();
  const deleteMutation = useDeleteDeliveryDestinationMutation();

  const isLoading = isLoadingCustomers && customers.length === 0;
  const isSaving = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const {
    name,
    name2,
    notes,
    customerId,
    code,
    reference,
    streetAddress,
    postalCode,
    city,
    country
  } = deliveryDestination;

  const isEdit = deliveryDestinationToEdit !== null;
  const customerOptions = customers.filter(c => c.isActive).map(mapCustomerToOptions);
  const selectedCustomer = customers.find(i => i.id === customerId) || null;
  const selectedCustomerOption = selectedCustomer && mapCustomerToOptions(selectedCustomer);

  const isValid = name.trim().length > 0 && !!customerId;
  const isSaveDisabled = isSaving || !isValid;

  const handleTextInputChanged = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDeliveryDestination({
      ...deliveryDestination,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomerChanged = (newValue: any, actionMeta: any) => {
    const value = actionMeta.action === 'clear' ? null : newValue.value;
    setDeliveryDestination({
      ...deliveryDestination,
      customerId: value
    });
  };

  const handleSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();

    if (isEdit) {
      updateMutation.mutate(deliveryDestination, { onSuccess: () => onClose() });
    } else {
      createMutation.mutate(deliveryDestination, { onSuccess: () => onClose() });
    }
  };

  const handleRemove = () => {
    if (confirm(t('deliveryDestinations.confirm_delete'))) {
      deleteMutation.mutate(deliveryDestination, { onSuccess: () => onClose() });
    }
  };

  return (
    <SideForm isLoading={isLoading} onEscape={onClose} onSubmit={handleSubmit}>
      {isEdit ? (
        <H3>
          {t('editing')}: <i>{deliveryDestinationToEdit?.name}</i>
        </H3>
      ) : (
        <H3>
          {t('add_new')} {t('deliveryDestinations.deliveryDestination').toLowerCase()}
        </H3>
      )}
      <div>
        <InputLabel htmlFor="customerId">{t('deliveryDestinations.customer')} *</InputLabel>
        <Select
          name="customerId"
          placeholder={t('select_customer')}
          onChange={handleCustomerChanged}
          options={customerOptions}
          value={selectedCustomerOption}
          styles={colourStyles}
          autoFocus
        />
      </div>
      <div>
        <InputLabel htmlFor="name">{t('deliveryDestinations.name')} *</InputLabel>
        <TextInput id="name" name="name" type="text" value={name} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="name2">{t('deliveryDestinations.name2')}</InputLabel>
        <TextInput id="name2" name="name2" type="text" value={name2} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="code">{t('deliveryDestinations.code')}</InputLabel>
        <TextInput id="code" name="code" type="text" value={code} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="reference">{t('deliveryDestinations.reference')}</InputLabel>
        <TextInput id="reference" name="reference" type="text" value={reference} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="streetAddress">{t('deliveryDestinations.streetAddress')}</InputLabel>
        <TextInput
          id="streetAddress"
          name="streetAddress"
          type="text"
          value={streetAddress}
          onChange={handleTextInputChanged}
        />
      </div>
      <div>
        <InputLabel htmlFor="postalCode">{t('deliveryDestinations.postalCode')}</InputLabel>
        <TextInput id="postalCode" name="postalCode" type="text" value={postalCode} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="city">{t('deliveryDestinations.city')}</InputLabel>
        <TextInput id="city" name="city" type="text" value={city} onChange={handleTextInputChanged} />
      </div>
      <div>
        <InputLabel htmlFor="country">{t('deliveryDestinations.country')}</InputLabel>
        <TextInput id="country" name="country" type="text" value={country} onChange={handleTextInputChanged} />
      </div>
      <InputLabel htmlFor="notes">{t('deliveryDestinations.notes')}</InputLabel>
      <div>
        <TextArea id="notes" name="notes" value={notes} onChange={handleTextInputChanged} />
      </div>
      <SidebarButtons>
        <Button type="button" secondary={true} disabled={isSaving} onClick={onClose}>
          {t('cancel')}
        </Button>
        <div />
        {isSaving && <OperationLoader title={t('saving') + '...'} />}
        <RemoveButton type="button" danger={true} disabled={isSaving} onClick={handleRemove}>
          {t('delete')}
        </RemoveButton>
        <Button type="submit" tertiary={true} disabled={isSaveDisabled}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default DeliveryDestinationEditor;
